const FALLBACK_CURRENCY = 'USD'

/** EUR: European number style with € after the amount (Kartly storefront parity). */
const EUR_NUMBER_LOCALE = 'de-DE'

/** ISO 4217 code for Intl, or fallback when missing/invalid. */
export function resolveCurrencyCode(code: unknown): string {
  const s = String(code ?? '')
    .trim()
    .toUpperCase()
  return /^[A-Z]{3}$/.test(s) ? s : FALLBACK_CURRENCY
}

/**
 * Formats an amount with a currency **sign** (₦, €, $), not the ISO code (NGN, EUR, USD).
 */
export function formatMoney(
  amount: number,
  currencyCode: string = FALLBACK_CURRENCY,
  locale?: string,
): string {
  if (!Number.isFinite(amount)) return '—'
  const code = resolveCurrencyCode(currencyCode)
  const loc =
    locale ?? (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US')

  try {
    if (code === 'EUR') {
      const number = new Intl.NumberFormat(EUR_NUMBER_LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)
      return `${number}\u00A0€`
    }

    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).format(amount)
  } catch {
    if (code === 'EUR') {
      return `${amount.toFixed(2).replace('.', ',')}\u00A0€`
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: FALLBACK_CURRENCY,
      currencyDisplay: 'narrowSymbol',
    }).format(amount)
  }
}

/** Strips non-numeric input; keeps one decimal point and up to two fraction digits. */
export function sanitizeMoneyInput(input: string): string {
  const cleaned = input.replace(/,/g, '').replace(/[^\d.]/g, '')
  const firstDot = cleaned.indexOf('.')
  if (firstDot === -1) return cleaned
  const intPart = cleaned.slice(0, firstDot)
  const rest = cleaned.slice(firstDot + 1).replace(/\./g, '')
  return intPart + '.' + rest.slice(0, 2)
}

/** Normalizes a stored decimal string for API (no grouping). */
export function normalizeMoneyString(raw: string): string {
  const s = raw.trim()
  if (s === '' || s === '.') return ''
  const n = parseFloat(s.replace(/,/g, ''))
  if (!Number.isFinite(n)) return ''
  return String(n)
}

export function formatMoneyFromUnknown(value: unknown, currencyCode: string = FALLBACK_CURRENCY): string {
  if (value == null || value === '') return '—'
  const n =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, '').replace(/[^\d.-]/g, ''))
  if (!Number.isFinite(n)) return String(value)
  return formatMoney(n, currencyCode)
}
